import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSave } from "@fortawesome/free-solid-svg-icons";
import moment from "moment-timezone";
import Select from "react-select";
import toast from "react-hot-toast";
import { GlobalContext } from "../../../utils/context/GlobalContextProvider.jsx";
import { ProjectApi } from "../../../api/authority/generated/index";
import { useErrorTranslation } from "../../../api/utilities/translateErrorMessage.js";
import { useStringQueryState } from "../../../utils/useQueryState.js";
import { useRenderCurrencyValue } from "../../../api/utilities/useValueRenderers.js";
import { useSupportedCurrencies, useSupportedCurrency } from "../../../api/utilities/useSupportedCurrencies.js";
import { useModalSelectProps } from "../../../utils/useModalSelectProps.js";
import Modal from "../../../components/Modal.jsx";
import YesNo from "../../../components/YesNo.jsx";
import Button from "../../../components/Button.jsx";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import MediumSecureLayout from "../../../layout/MediumSecureLayout.jsx";
import { OtherSystemType } from "../../../api/authority/generated/models/OtherSystemType.ts";
import { OtherSystemCategory } from "../../../api/authority/generated/models/OtherSystemCategory.ts";
import ActionButton from "../../../components/ActionButton.jsx";
import { launchOtherSystemRequest, otherSystemDataViewState } from "./otherSystemRequestLifecycle.js";
import {getOtherSystemCategoryOptions, otherSystemCategoryLabel} from "../../../utils/otherSystemCategoryPresentation.js";
import {sourceMarketingFlagForForm, sourceMarketingFlagPayload, sourceMarketingFlagVisible} from "../../../utils/otherSystemMarketingFlag.js";

export {otherSystemCategoryLabel};

// Custom hook for fetching and managing sources
const useSources = (projectId, otherSystemType, getAuthorityConfiguration, et) => {
    const [sources, setSources] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!projectId || !otherSystemType) {
            setSources(null);
            setIsLoading(false);
            return;
        }

        let active = true;
        const controller = new AbortController();
        setIsLoading(true);
        setSources(null);
        setHasError(false);
        const api = new ProjectApi(getAuthorityConfiguration());
        launchOtherSystemRequest(({signal}) => api.otherSystemSources(
            {otherSystemType, projectId},
            {signal},
        ), {
            signal: controller.signal,
            isActive: () => active,
            onSuccess: (data) => { setSources(data); setHasError(false); },
            onError: (error) => {
                setHasError(true);
                et(error).then((message) => { if (active && message) toast.error(message); }).catch(() => {});
            },
            onFinally: () => setIsLoading(false),
        });

        return () => {
            active = false;
            controller.abort();
        };
    }, [projectId, otherSystemType, getAuthorityConfiguration, et]);

    return { sources, isLoading, hasError, setSources };
};

// Custom hook for managing selected source
const useSelectedSource = (sources) => {
    const [selectedSourceId, setSelectedSourceId] = useStringQueryState('selectedSource', null);
    const prevSources = useRef(null);

    const stableSetSelectedSourceId = useCallback((id) => {
        setSelectedSourceId(id);
    }, [setSelectedSourceId]);

    useEffect(() => {
        if (!sources?.length) return;

        const isValidSelection = sources.some(source => source.otherSystemSource.id === selectedSourceId);
        if (prevSources.current !== sources || !isValidSelection) {
            stableSetSelectedSourceId(sources[0].otherSystemSource.id);
        }
        prevSources.current = sources;
    }, [sources, selectedSourceId, stableSetSelectedSourceId]);

    const selectedSourceName = useMemo(() => {
        return sources?.find(source => source.otherSystemSource.id === selectedSourceId)?.otherSystemSource.name || null;
    }, [sources, selectedSourceId]);

    return { selectedSourceId, setSelectedSourceId: stableSetSelectedSourceId, selectedSourceName };
};

// Custom hook for fetching source insights
const useSourceInsights = (projectId, otherSystemType, selectedSourceId, getAuthorityConfiguration, et) => {
    const [selectedSourceData, setSelectedSourceData] = useState(null);
    const [hasError, setHasError] = useState(false);
    const [reloadVersion, setReloadVersion] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const stableSetReloadSource = useCallback(() => {
        setReloadVersion((version) => version + 1);
    }, []);

    useEffect(() => {
        if (!selectedSourceId) {
            setSelectedSourceData(null);
            setHasError(false);
            setIsLoading(false);
            return;
        }

        let active = true;
        const controller = new AbortController();
        setIsLoading(true);
        setSelectedSourceData(null);
        setHasError(false);
        const api = new ProjectApi(getAuthorityConfiguration());
        launchOtherSystemRequest(({signal}) => api.otherSystemSourceInsights(
            {otherSystemType, projectId, otherSystemSourceId: selectedSourceId},
            {signal},
        ), {
            signal: controller.signal,
            isActive: () => active,
            onSuccess: (data) => { setSelectedSourceData(data); setHasError(false); },
            onError: (error) => {
                setHasError(true);
                et(error).then((message) => { if (active && message) toast.error(message); }).catch(() => {});
            },
            onFinally: () => setIsLoading(false),
        });

        return () => {
            active = false;
            controller.abort();
        };
    }, [selectedSourceId, reloadVersion, projectId, otherSystemType, getAuthorityConfiguration, et]);

    return { selectedSourceData, setReloadSource: stableSetReloadSource, isLoading, hasError };
};

// Custom hook for managing insight form (create and modify)
const useInsightForm = (project, selectedSourceId, otherSystemType, getAuthorityConfiguration, et, setReloadSource) => {
    const defaultCreateFormData = useMemo(() => ({
        since: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        until: moment().format('YYYY-MM-DD'),
        spend: { amount: 0, currency: project.currency },
        note: null
    }), [project.currency]);

    const [formData, setFormData] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (!formSubmitted) return;

        if (isEditMode) {
            new ProjectApi(getAuthorityConfiguration())
                .modifyOtherSystemSourceInsights({
                    projectId: project.id,
                    otherSystemType: otherSystemType,
                    otherSystemSourceId: selectedSourceId,
                    modifyOtherSystemSourceInsightInput: formData,
                })
                .then(() => {
                    setFormData(null);
                    setReloadSource();
                })
                .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
                .finally(() => setFormSubmitted(false));
        } else {
            new ProjectApi(getAuthorityConfiguration())
                .createOtherSystemSourceInsights({
                    projectId: project.id,
                    otherSystemType: otherSystemType,
                    otherSystemSourceId: selectedSourceId,
                    createOtherSystemSourceInsightInput: formData,
                })
                .then(() => {
                    setFormData(null);
                    setReloadSource();
                })
                .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
                .finally(() => setFormSubmitted(false));
        }
    }, [formSubmitted, formData, isEditMode, project.id, otherSystemType, selectedSourceId, getAuthorityConfiguration, et, setReloadSource]);

    return { formData, setFormData, formSubmitted, setFormSubmitted, isEditMode, setIsEditMode, defaultCreateFormData };
};

// Custom hook for managing source form (create and modify)
const useSourceForm = (project, otherSystemType, getAuthorityConfiguration, et, setReloadSources) => {
    const { t } = useTranslation();
    const defaultCreateFormData = useMemo(() => ({
        name: '',
        category: null,
        domainId: null,
        centreId: null,
        includeInMarketingCosts: false,
    }), []);

    const [formData, setFormData] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editSourceId, setEditSourceId] = useState(null);
    const [projectDomains, setProjectDomains] = useState([]);
    const [projectCentres, setProjectCentres] = useState([]);
    const isSourceFormOpen = formData !== null;

    const isFormValid = useMemo(() => {
        return formData?.name?.trim() &&
            (otherSystemType !== OtherSystemType.Other || formData?.category) &&
            formData?.domainId &&
            formData?.centreId;
    }, [formData, otherSystemType]);

    const categoryOptions = useMemo(() => getOtherSystemCategoryOptions(t), [t]);

    const domainOptions = useMemo(() =>
        projectDomains.map(domain => ({
            value: domain.projectDomain.id,
            label: domain.projectDomain.hostName,
        }))
    , [projectDomains]);

    const centreOptions = useMemo(() => {
        return projectCentres.map(({ projectCentre }) => ({
            value: projectCentre.id,
            label: projectCentre.name,
        }));
    }, [projectCentres]);

    useEffect(() => {
        if (!project.id || !isSourceFormOpen) return;

        let active = true;
        const controller = new AbortController();
        const api = new ProjectApi(getAuthorityConfiguration());
        launchOtherSystemRequest(({signal}) => Promise.all([
            api.listProjectDomains({projectId: project.id}, {signal}),
            api.listProjectCentres({projectId: project.id}, {signal}),
        ]), {
            signal: controller.signal,
            isActive: () => active,
            onSuccess: ([domains, centres]) => {
                setProjectDomains(domains);
                setProjectCentres(centres);
            },
            onError: () => {
                setProjectDomains([]);
                setProjectCentres([]);
            },
            onFinally: () => {},
        });

        return () => {
            active = false;
            controller.abort();
        };
    }, [project.id, isSourceFormOpen, getAuthorityConfiguration]);

    useEffect(() => {
        if (!formSubmitted) return;

        if (isEditMode) {
            new ProjectApi(getAuthorityConfiguration())
                .modifyOtherSystemSource({
                    projectId: project.id,
                    otherSystemType,
                    otherSystemSourceId: editSourceId,
                    modifyOtherSystemSourceInput: sourceMarketingFlagPayload(otherSystemType, formData),
                })
                .then(() => {
                    setFormData(null);
                    setEditSourceId(null);
                    setIsEditMode(false);
                    setReloadSources(true);
                })
                .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
                .finally(() => setFormSubmitted(false));
        } else {
            new ProjectApi(getAuthorityConfiguration())
                .createOtherSystemSource({
                    projectId: project.id,
                    otherSystemType,
                    createOtherSystemSourceInput: {
                        ...sourceMarketingFlagPayload(otherSystemType, formData),
                        // The existing API requires a category string; Authority stores no
                        // category for Mailing/Affiliate, so it ignores this compatibility value.
                        category: formData.category ?? OtherSystemCategory.Other,
                    },
                })
                .then(() => {
                    setFormData(null);
                    setReloadSources(true);
                })
                .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
                .finally(() => setFormSubmitted(false));
        }
    }, [formSubmitted, formData, isEditMode, editSourceId, project.id, otherSystemType, getAuthorityConfiguration, et, setReloadSources]);

    return {
        formData,
        setFormData,
        formSubmitted,
        setFormSubmitted,
        isEditMode,
        setIsEditMode,
        editSourceId,
        setEditSourceId,
        defaultCreateFormData,
        categoryOptions,
        domainOptions,
        centreOptions,
        isFormValid,
    };
};

// Custom hook for managing insight deletion
const useDeleteInsight = (projectId, otherSystemType, selectedSourceId, getAuthorityConfiguration, et, setReloadSource) => {
    const [removeInsightId, setRemoveInsightId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Handle deletion of an insight
    const handleDeleteInsight = useCallback(async () => {
        if (!removeInsightId || !selectedSourceId) return;

        setIsDeleting(true);
        try {
            await new ProjectApi(getAuthorityConfiguration())
                .deleteOtherSystemSourceInsight({
                    projectId,
                    otherSystemType,
                    otherSystemSourceId: selectedSourceId,
                    deleteOtherSystemSourceInsightInput: { id: removeInsightId },
                });
            setReloadSource();
        } catch (e) {
            et(e).then((message) => { if (message) toast.error(message); });
        } finally {
            setRemoveInsightId(null);
            setIsDeleting(false);
        }
    }, [removeInsightId, selectedSourceId, projectId, otherSystemType, getAuthorityConfiguration, et, setReloadSource]);

    return { removeInsightId, setRemoveInsightId, isDeleting, handleDeleteInsight };
};

// Custom hook for managing source deletion
const useDeleteSource = (projectId, otherSystemType, selectedSourceId, setSelectedSourceId, getAuthorityConfiguration, et, setReloadSources) => {
    const [removeSourceId, setRemoveSourceId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // Handle deletion of a source
    const handleDeleteSource = useCallback(async () => {
        if (!removeSourceId) return;

        setIsDeleting(true);
        try {
            await new ProjectApi(getAuthorityConfiguration())
                .deleteOtherSystemSource({
                    projectId,
                    otherSystemType,
                    otherSystemSourceId: removeSourceId,
                });
            setReloadSources(true);
            // If the deleted source was selected, select the first available source
            if (removeSourceId === selectedSourceId) {
                setSelectedSourceId(null); // Will be updated by useSelectedSource
            }
        } catch (e) {
            et(e).then((message) => { if (message) toast.error(message); });
        } finally {
            setRemoveSourceId(null);
            setIsDeleting(false);
        }
    }, [removeSourceId, selectedSourceId, projectId, otherSystemType, getAuthorityConfiguration, et, setReloadSources, setSelectedSourceId]);

    return { removeSourceId, setRemoveSourceId, isDeleting, handleDeleteSource };
};

// Component for rendering source list
const SourceList = memo(({ sources, selectedSourceId, setSelectedSourceId, otherSystemType, t, setFormData, defaultCreateFormData, formData, setIsEditMode, setEditSourceId, setRemoveSourceId }) => {
    const [selectedCategory, setSelectedCategory] = useState(null);

    // Create a list of unique categories from loaded sources
    const categoryOptions = useMemo(() => {
        const categories = [...new Set(sources?.map(source => source.otherSystemSource.category))];
        return [
            { value: null, label: t('other-sources.categories.all') },
            ...categories.map(category => ({
                value: category,
                label: otherSystemCategoryLabel(t, category)
            }))
        ];
    }, [sources, t]);

    // Count of unique categories
    const uniqueCategoryCount = useMemo(() => {
        return new Set(sources?.map(source => source.otherSystemSource.category)).size;
    }, [sources]);

    // Filtered sources based on selected category
    const filteredSources = useMemo(() => {
        if (!selectedCategory) return sources;
        return sources?.filter(source => source.otherSystemSource.category === selectedCategory);
    }, [sources, selectedCategory]);

    // Update selectedSourceId if it is no longer valid after filtering
    useEffect(() => {
        if (!filteredSources?.length) return;

        const isValidSelection = filteredSources.some(source => source.otherSystemSource.id === selectedSourceId);
        if (!isValidSelection) {
            setSelectedSourceId(filteredSources[0].otherSystemSource.id);
        }
    }, [filteredSources, selectedSourceId, setSelectedSourceId]);

    return (
        <div
            className={`flex flex-nowrap flex-col md:flex-row md:block px-3 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700/60 min-w-60 md:space-y-3 ${
                otherSystemType === OtherSystemType.Other ? 'pt-4 pb-6' : 'py-6'
            }`}
        >
            <div className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase mb-3 flex items-center justify-between">
                {t('other-sources.sources')}
                {(
                    otherSystemType === OtherSystemType.Other ||
                    otherSystemType === OtherSystemType.Mailing ||
                    otherSystemType === OtherSystemType.Affiliate
                ) && (
                    <Button
                        className="flex gap-2 items-center"
                        variant="black"
                        onClick={() => {
                            setFormData(formData ? null : defaultCreateFormData);
                            setIsEditMode(false);
                            setEditSourceId(null);
                        }}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        {t('other-sources.add')}
                    </Button>
                )}
            </div>
            {uniqueCategoryCount > 1 && sources.length > 5 && (
                <div className="source-category-filter mb-3">
                    <select
                        className="form-input w-full py-1 text-sm"
                        value={selectedCategory || ''}
                        onChange={(e) => setSelectedCategory(e.target.value || null)}
                    >
                        {categoryOptions.map(option => (
                            <option key={option.value || 'all'} value={option.value || ''}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            <ul className="flex flex-nowrap md:block mr-3 md:mr-0 overflow-x-scroll no-scrollbar md:overflow-auto">
                {filteredSources?.map((source) => (
                    <li
                        className="os-source mr-0.5 md:mr-0 md:mb-0.5 select-none cursor-pointer relative text-nowrap md:text-wrap"
                        key={source.otherSystemSource.id}
                    >
                        <div
                            className={`flex flex-col items-left px-2.5 py-2 rounded-lg ${
                                source.otherSystemSource.id === selectedSourceId
                                    ? 'bg-[linear-gradient(135deg,var(--tw-gradient-stops))] from-violet-500/[0.12] dark:from-violet-500/[0.24] to-violet-500/[0.04]'
                                    : ''
                            }`}
                            onClick={() => setSelectedSourceId(source.otherSystemSource.id)}
                        >
                            {otherSystemType === OtherSystemType.Other && (
                                <span className={
                                    'text-xxs border-1 self-start ' +
                                    'border-violet-400 dark:border-gray-700/60 ' +
                                    'text-violet-800 dark:text-gray-300 ' +
                                    'px-1.5 py-0.5 rounded-full shadow-none'
                                }>
                                    {otherSystemCategoryLabel(t, source.otherSystemSource.category)}
                                </span>
                            )}
                            <div className="text-sm font-medium text-black dark:text-gray-300">
                                {source.otherSystemSource.name}
                            </div>
                            {source.projectDomain && (
                                <div className="text-xxs text-gray-500">
                                    {t('project.domain')}: {source.projectDomain.hostName}
                                </div>
                            )}
                            {source.projectCentre && (
                                <div className="text-xxs text-gray-500">
                                    {t('orders.centre')}: {source.projectCentre.name}
                                </div>
                            )}
                        </div>
                        {otherSystemType === OtherSystemType.Other && (
                            <div className={'source-btns flex gap-1 absolute right-1 top-1 p-1 bg-white rounded-lg opacity-0 dark:bg-gray-800'}>
                                <ActionButton
                                    icon={'pencil'}
                                    onClick={() => {
                                        setFormData({
                                            name: source.otherSystemSource.name,
                                            category: source.otherSystemSource.category,
                                            domainId: source.projectDomain?.id || null,
                                            centreId: source.projectCentre?.id || null,
                                            includeInMarketingCosts: sourceMarketingFlagForForm(source.otherSystemSource.includeInMarketingCosts),
                                        });
                                        setIsEditMode(true);
                                        setEditSourceId(source.otherSystemSource.id);
                                    }}
                                />
                                <ActionButton
                                    icon={'trash'}
                                    variant={'primary-red'}
                                    onClick={() => setRemoveSourceId(source.otherSystemSource.id)}
                                />
                            </div>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
});

// Component for rendering insight modal (create and modify)
const InsightModal = memo(({ formData, setFormData, formSubmitted, setFormSubmitted, isEditMode, selectedSourceName, possibleCurrencies, defaultCurrencyValue, possibleCurrency, modalSelectProps, t, otherSystemType }) => {
    const isFormValid = useMemo(() => {
        return formData?.spend?.amount > 0;
    }, [formData]);

    const title = isEditMode ? t('other-sources.spend-edit') : t('other-sources.spend-add');
    const buttonText = isEditMode ? t('save-changes') : t('other-sources.spend-add');

    return (
        <Modal
            visible={formData !== null}
            setVisible={(visible) => setFormData(visible ? formData : null)}
            loading={formSubmitted}
            title={title}
            subtitle={selectedSourceName}
            className="max-w-lg mt-0 sm:mt-auto"
        >
            {formData && (
                <>
                    <div className="flex flex-wrap sm:flex-nowrap gap-x-4 gap-y-2">
                        <input
                            type="date"
                            className="form-input w-full sm:w-1/2"
                            value={formData.since}
                            onChange={(e) =>
                                setFormData({ ...formData, since: e.target.value })
                            }
                        />
                        <input
                            type="date"
                            className="form-input w-full sm:w-1/2"
                            value={formData.until}
                            onChange={(e) =>
                                setFormData({ ...formData, until: e.target.value })
                            }
                        />
                    </div>
                    <div className="flex gap-x-4 gap-y-2 mt-2 flex-wrap sm:flex-nowrap">
                        <input
                            type="number"
                            className="form-input flex-1 shrink-1"
                            step="1"
                            min="0"
                            value={formData.spend.amount === 0 ? '' : formData.spend.amount / 100}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    spend: { ...formData.spend, amount: e.target.value === '' ? 0 : e.target.value * 100 },
                                })
                            }
                        />
                        <Select
                            {...modalSelectProps}
                            value={isEditMode ? possibleCurrency(formData.spend.currency) : defaultCurrencyValue}
                            options={possibleCurrencies}
                            className="shrink-0 grow-0 min-w-28"
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    spend: { ...formData.spend, currency: e.value },
                                })
                            }
                        />
                    </div>
                    {sourceMarketingFlagVisible(otherSystemType) && (
                        <div className="mt-4">
                            <label className="block text-sm font-medium mb-1">{t('other-sources.note')}</label>
                            <textarea
                                className="form-input w-full"
                                rows="4"
                                value={formData.note || ''}
                                onChange={(e) =>
                                    setFormData({ ...formData, note: e.target.value || null })
                                }
                            />
                        </div>
                    )}
                    <div className="mt-4 flex">
                        <Button
                            className="ml-auto flex gap-2 items-center"
                            variant="green"
                            onClick={() => setFormSubmitted(true)}
                            disabled={!isFormValid}
                        >
                            {isEditMode && <FontAwesomeIcon icon={faSave} />}
                            {buttonText}
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
});

// Component for rendering source modal (create and modify)
const SourceModal = memo(({ formData, setFormData, formSubmitted, setFormSubmitted, isEditMode, categoryOptions, domainOptions, centreOptions, modalSelectProps, t, isFormValid, otherSystemType }) => {
    const title = isEditMode ? t('other-sources.edit-source') : t('other-sources.add-source');
    const buttonText = isEditMode ? t('save-changes') : t('other-sources.add-source');

    return (
        <Modal
            visible={formData !== null}
            setVisible={(visible) => setFormData(visible ? formData : null)}
            loading={formSubmitted}
            title={title}
            className="max-w-lg mt-0 sm:mt-auto"
        >
            {formData && (
                <>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">{t('other-sources.name')}</label>
                        <input
                            type="text"
                            className="form-input w-full"
                            value={formData.name}
                            onChange={(e) =>
                                setFormData({ ...formData, name: e.target.value })
                            }
                        />
                    </div>
                    {otherSystemType === OtherSystemType.Other && <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">{t('other-sources.category')}</label>
                        <Select
                            {...modalSelectProps}
                            value={categoryOptions.find(option => option.value === formData.category)}
                            options={categoryOptions}
                            className="w-full"
                            onChange={(e) =>
                                setFormData({ ...formData, category: e.value })
                            }
                        />
                    </div>}
                    {otherSystemType === OtherSystemType.Other && (
                        <label className="mb-4 flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={formData.includeInMarketingCosts === true}
                                onChange={(e) => setFormData({ ...formData, includeInMarketingCosts: e.target.checked })}
                            />
                            <span>{t('other-sources.include-in-marketing-costs')}</span>
                        </label>
                    )}
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">{t('other-sources.domain')}</label>
                        <Select
                            {...modalSelectProps}
                            value={domainOptions.find(option => option.value === formData.domainId) || null}
                            options={domainOptions}
                            className="w-full"
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    domainId: e ? e.value : null,
                                })
                            }
                            isClearable
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium mb-1">{t('other-sources.centre')}</label>
                        <Select
                            {...modalSelectProps}
                            value={centreOptions.find(option => option.value === formData.centreId) || null}
                            options={centreOptions}
                            className="w-full"
                            onChange={(e) =>
                                setFormData({ ...formData, centreId: e ? e.value : null })
                            }
                            isClearable
                        />
                    </div>
                    <div className="mt-4 flex">
                        <Button
                            className="ml-auto flex gap-2 items-center"
                            variant="green"
                            onClick={() => setFormSubmitted(true)}
                            disabled={!isFormValid}
                        >
                            {isEditMode && <FontAwesomeIcon icon={faSave} />}
                            {buttonText}
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
});

// Main component
export default function OtherSystemSettings({ project, otherSystemType }) {
    const { getAuthorityConfiguration } = useContext(GlobalContext);
    const { t } = useTranslation();
    const { et: originalEt } = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const modalSelectProps = useModalSelectProps();
    const possibleCurrencies = useSupportedCurrencies();
    const possibleCurrency = useSupportedCurrency();

    const et = useCallback((error) => originalEt(error), [originalEt]);

    const title = useMemo(() => {
        const translations = {
            'Mailing': t('route.mailing-spends'),
            'Affiliate': t('route.affiliate-spends'),
            'Other': t('route.other-spends'),
        };
        return translations[otherSystemType];
    }, [otherSystemType, t]);

    const [reloadSources, setReloadSources] = useState(false);

    const { sources, isLoading: sourcesLoading, hasError: sourcesError, setSources } = useSources(project.id, otherSystemType, getAuthorityConfiguration, et);
    const { selectedSourceId, setSelectedSourceId, selectedSourceName } = useSelectedSource(sources);
    const { selectedSourceData, setReloadSource, isLoading: insightsLoading, hasError: insightsError } = useSourceInsights(project.id, otherSystemType, selectedSourceId, getAuthorityConfiguration, et);
    const { formData: insightFormData, setFormData: setInsightFormData, formSubmitted: insightFormSubmitted, setFormSubmitted: setInsightFormSubmitted, isEditMode: isInsightEditMode, setIsEditMode: setInsightEditMode, defaultCreateFormData } = useInsightForm(project, selectedSourceId, otherSystemType, getAuthorityConfiguration, et, setReloadSource);
    const { removeInsightId, setRemoveInsightId, isDeleting: isDeletingInsight, handleDeleteInsight } = useDeleteInsight(project.id, otherSystemType, selectedSourceId, getAuthorityConfiguration, et, setReloadSource);
    const { removeSourceId, setRemoveSourceId, isDeleting: isDeletingSource, handleDeleteSource } = useDeleteSource(project.id, otherSystemType, selectedSourceId, setSelectedSourceId, getAuthorityConfiguration, et, setReloadSources);
    const {
        formData: sourceFormData,
        setFormData: setSourceFormData,
        formSubmitted: sourceFormSubmitted,
        setFormSubmitted: setSourceFormSubmitted,
        isEditMode: isSourceEditMode,
        setIsEditMode: setSourceIsEditMode,
        editSourceId,
        setEditSourceId,
        defaultCreateFormData: defaultCreateSourceFormData,
        categoryOptions,
        domainOptions,
        centreOptions,
        isFormValid: isSourceFormValid,
    } = useSourceForm(project, otherSystemType, getAuthorityConfiguration, et, setReloadSources);

    const defaultCurrencyValue = useMemo(() => {
        return possibleCurrency(defaultCreateFormData.spend.currency) || {};
    }, [defaultCreateFormData, possibleCurrency]);

    // Find the name of the source to be deleted
    const sourceToDeleteName = useMemo(() => {
        const source = sources?.find(s => s.otherSystemSource.id === removeSourceId);
        return source ? source.otherSystemSource.name : removeSourceId;
    }, [sources, removeSourceId]);

    useEffect(() => {
        if (!reloadSources) return;
        let active = true;
        const controller = new AbortController();
        const api = new ProjectApi(getAuthorityConfiguration());
        launchOtherSystemRequest(({signal}) => api.otherSystemSources(
            {otherSystemType, projectId: project.id},
            {signal},
        ), {
            signal: controller.signal,
            isActive: () => active,
            onSuccess: setSources,
            onError: (error) => {
                et(error).then((message) => { if (active && message) toast.error(message); }).catch(() => {});
            },
            onFinally: () => setReloadSources(false),
        });

        return () => {
            active = false;
            controller.abort();
        };
    }, [reloadSources, project.id, otherSystemType, getAuthorityConfiguration, et, setSources]);

    return [
        <YesNo
            key="other-system-insight-yes-no"
            visible={removeInsightId !== null}
            setVisible={() => setRemoveInsightId(null)}
            title={t('other-sources.really-remove-insight')}
            question={<span>Id: {removeInsightId}</span>}
            onYes={handleDeleteInsight}
            onNo={() => setRemoveInsightId(null)}
            loading={isDeletingInsight}
        />,
        <YesNo
            key="other-system-source-yes-no"
            visible={removeSourceId !== null}
            setVisible={() => setRemoveSourceId(null)}
            title={t('other-sources.remove-source-title')}
            question={<span>{sourceToDeleteName}</span>}
            onYes={handleDeleteSource}
            onNo={() => setRemoveSourceId(null)}
            loading={isDeletingSource}
        />,
        <InsightModal
            key="other-system-insight-modal"
            formData={insightFormData}
            setFormData={setInsightFormData}
            formSubmitted={insightFormSubmitted}
            setFormSubmitted={setInsightFormSubmitted}
            isEditMode={isInsightEditMode}
            selectedSourceName={selectedSourceName}
            possibleCurrencies={possibleCurrencies}
            defaultCurrencyValue={defaultCurrencyValue}
            possibleCurrency={possibleCurrency}
            modalSelectProps={modalSelectProps}
            t={t}
            otherSystemType={otherSystemType}
        />,
        <SourceModal
            key="other-system-source-modal"
            formData={sourceFormData}
            setFormData={setSourceFormData}
            formSubmitted={sourceFormSubmitted}
            setFormSubmitted={setSourceFormSubmitted}
            isEditMode={isSourceEditMode}
            categoryOptions={categoryOptions}
            domainOptions={domainOptions}
            centreOptions={centreOptions}
            modalSelectProps={modalSelectProps}
            t={t}
            isFormValid={isSourceFormValid}
            otherSystemType={otherSystemType}
        />,
        <MediumSecureLayout title={title} key="other-system-source-insights">
            {otherSystemDataViewState({loading: sourcesLoading, error: sourcesError, data: sources}) === 'pending' ? (
                <div className="text-center pt-10">{t('other-sources.loading-sources')}</div>
            ) : otherSystemDataViewState({loading: sourcesLoading, error: sourcesError, data: sources}) === 'error' ? (
                <div className="text-center pt-10" role="alert">{t('reporting_lab.data_quality.load_error')}</div>
            ) : otherSystemDataViewState({loading: sourcesLoading, error: sourcesError, data: sources}) === 'populated' ? (
                <LoadingComponent className="w-full max-w-9xl mx-auto" loading={sourcesLoading}>
                    <div className="bg-white dark:bg-gray-800 shadow-sm app-surface rounded-xl mb-8">
                        <div className="flex flex-col md:flex-row md:-mr-px">
                            <SourceList
                                sources={sources}
                                selectedSourceId={selectedSourceId}
                                setSelectedSourceId={setSelectedSourceId}
                                otherSystemType={otherSystemType}
                                t={t}
                                setFormData={setSourceFormData}
                                defaultCreateFormData={defaultCreateSourceFormData}
                                formData={sourceFormData}
                                setIsEditMode={setSourceIsEditMode}
                                setEditSourceId={setEditSourceId}
                                setRemoveSourceId={setRemoveSourceId}
                            />
                            <div className="grow">
                                <div className="flex justify-between mb-3 px-6 pt-4 mb-5 items-center">
                                    <div className="text-2xl text-gray-800 dark:text-gray-100 font-bold">
                                        {t('other-sources.spends')}
                                    </div>
                                    <Button
                                        className="flex gap-2 items-center"
                                        variant="green"
                                        onClick={() => {
                                            setInsightFormData(defaultCreateFormData);
                                            setInsightEditMode(false);
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                        {t('other-sources.spend-add')}
                                    </Button>
                                </div>
                                <div className="px-6 pb-6">
                                    <LoadingComponent loading={insightsLoading}>
                                        {otherSystemDataViewState({loading: insightsLoading, error: insightsError, data: selectedSourceData}) === 'pending' && t('other-sources.loading-source')}
                                        {otherSystemDataViewState({loading: insightsLoading, error: insightsError, data: selectedSourceData}) === 'error' && <div role="alert">{t('reporting_lab.data_quality.load_error')}</div>}
                                        {selectedSourceData?.map((insight) => (
                                            <div
                                                className={
                                                    'bg-white dark:bg-slate-800 app-surface rounded-md ' +
                                                    'border radius-2 border-slate-300 dark:border-slate-700 overflow-hidden px-3 py-1.5 mb-1.5 ' +
                                                    'text-sm'
                                                }
                                                key={insight.id}
                                            >
                                                <div className={"flex gap-6 justify-between items-center"}>
                                                    <div>{moment(insight.since).format('DD. MM. YYYY')}</div>
                                                    <div>{moment(insight.until).format('DD. MM. YYYY')}</div>
                                                    <div className="font-bold">{renderCurrencyValue(insight.spend)}</div>
                                                    <div className={'buttons flex gap-1'}>
                                                        <ActionButton
                                                            icon={'pencil'}
                                                            onClick={() => {
                                                                setInsightFormData(insight);
                                                                setInsightEditMode(true);
                                                            }}
                                                        />
                                                        <ActionButton
                                                            icon={'trash'}
                                                            variant={'primary-red'}
                                                            onClick={() => setRemoveInsightId(insight.id)}
                                                        />
                                                    </div>
                                                </div>
                                                {insight.note && (
                                                    <div className="bottom pt-1 mt-2 border-t border-slate-100">
                                                        {insight.note}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </LoadingComponent>
                                </div>
                            </div>
                        </div>
                    </div>
                </LoadingComponent>
            ) : (
                <div className="text-center pt-10 flex flex-col items-center">
                    {t('other-sources.no-sources-found')}
                    {(
                        otherSystemType === OtherSystemType.Other ||
                        otherSystemType === OtherSystemType.Mailing ||
                        otherSystemType === OtherSystemType.Affiliate
                    ) && (
                        <Button
                            className="mt-4 flex gap-2 items-center"
                            variant="black"
                            onClick={() => {
                                setSourceFormData(sourceFormData ? null : defaultCreateSourceFormData);
                                setSourceIsEditMode(false);
                                setEditSourceId(null);
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            {t('other-sources.add-source')}
                        </Button>
                    )}
                </div>
            )}
        </MediumSecureLayout>,
    ];
}
